import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactElement,
  Ref,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import styled, {CSSProperties} from 'styled-components';
import {useCombinedRefs} from '../../../../hooks';
import {Input} from '../../../atoms/input';
import {Label} from '../../../atoms/label';
import {FieldWrapper} from '../../../atoms/field-wrapper';
import {Helper} from '../../../atoms/helper';
import {InputState} from '../../../interface';
import {InputNoBorder} from '../../../atoms/input/input';
import {Input as InputArea} from '../../../atoms/textArea';

export interface TextFieldProps extends ComponentPropsWithoutRef<'input'> {
  label?: string;
  optional?: boolean;
  state?: InputState;
  helper?: string | ReactElement;
  action?: (ref: RefObject<HTMLInputElement>) => ReactElement;
  margin?: boolean;
  inputStyle?: CSSProperties;
  actionStyle?: CSSProperties;
  noBorder?: boolean;
  showCancel?: boolean;
  handleCancel?: () => void;
  autoResize?: boolean;
  minTextareaHeight?: number;
  placeholderFontSize?: string;
}

const Wrapper = styled.div`
  position: relative;
`;

const InputWrapper = styled.div<{
  hasLabel?: boolean;
  $placeholderFontSize?: string;
}>`
  padding-top: ${({hasLabel}) => (hasLabel ? '0' : '0')};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  textarea {
    order: 2;
  }
  input {
    order: 2;
  }
  input::placeholder,
  textarea::placeholder {
    font-size: ${({$placeholderFontSize}) => $placeholderFontSize || 'inherit'};
  }
  label {
    order: 1;
  }
`;

const ActionWrapper = styled.span`
  position: absolute;
  right: 16px;
  bottom: 14px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const TextField = forwardRef(
  (
    {
      label,
      optional,
      placeholder,
      state,
      helper,
      action,
      margin,
      inputStyle,
      style,
      actionStyle,
      noBorder,
      autoResize = false,
      minTextareaHeight = 86,
      placeholderFontSize,
      onInput,
      value,
      ...rest
    }: TextFieldProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = useCombinedRefs<HTMLInputElement>(
      ref as RefObject<HTMLInputElement>,
      inputRef,
    );

    const resizeTextarea = useCallback(
      (textarea?: HTMLTextAreaElement | null) => {
        if (!autoResize || !textarea) return;

        textarea.style.height = 'auto';
        textarea.style.height = `${Math.max(
          textarea.scrollHeight,
          minTextareaHeight,
        )}px`;
        textarea.style.overflowY = 'hidden';
      },
      [autoResize, minTextareaHeight],
    );

    useLayoutEffect(() => {
      if (!rest.multiple) return;

      resizeTextarea(
        (inputRef.current as unknown) as HTMLTextAreaElement | null,
      );
    }, [resizeTextarea, rest.multiple, value]);

    const InputComponent =
      noBorder && !rest?.multiple
        ? InputNoBorder
        : rest?.multiple
        ? (InputArea as any)
        : Input;

    return (
      <FieldWrapper helper={!!helper} margin={margin} style={style}>
        <Wrapper>
          <InputWrapper
            hasLabel={!!label}
            $placeholderFontSize={placeholderFontSize}>
            <InputComponent
              aria-label="input"
              placeholder={placeholder ?? label}
              ref={combinedRef}
              onWheelCapture={(e: {currentTarget: {blur: () => void}}) => {
                e.currentTarget.blur();
              }}
              state={state}
              style={inputStyle}
              value={value}
              {...rest}
              onInput={(event: React.FormEvent<HTMLInputElement>) => {
                if (rest.multiple) {
                  resizeTextarea(
                    (event.currentTarget as unknown) as HTMLTextAreaElement,
                  );
                }
                onInput?.(event);
              }}
            />

            {action && (
              <ActionWrapper style={actionStyle}>
                {action(combinedRef)}
              </ActionWrapper>
            )}
            <Label aria-label="label" style={{position: 'unset'}}>
              {label}{' '}
              <span style={{color: '#5F5F8C', fontWeight: 400}}>
                {optional ? '(Optional)' : ''}
              </span>
            </Label>
          </InputWrapper>
          <Helper
            style={{textAlign: 'left'}}
            aria-label="helper"
            state={state}
            children={helper}
          />
        </Wrapper>
      </FieldWrapper>
    );
  },
);

export const TextFieldCustomInput = forwardRef(
  (
    {
      label,

      state,
      helper,
      action,
      margin,

      style,
      children,
      ...rest
    }: TextFieldProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = useCombinedRefs<HTMLInputElement>(
      ref as RefObject<HTMLInputElement>,
      inputRef,
    );

    return (
      <FieldWrapper helper={!!helper} margin={margin} style={style}>
        <Wrapper>
          <InputWrapper hasLabel={!!label}>
            {children}
            {action && <ActionWrapper>{action(combinedRef)}</ActionWrapper>}
            <Label aria-label="label">{label}</Label>
          </InputWrapper>
          <Helper aria-label="helper" state={state} children={helper} />
        </Wrapper>
      </FieldWrapper>
    );
  },
);
